import { debugLog } from "../../../configs/loggingConfig";

/**
 * Default error message for bad files
 */
export const defaultReadErrorMsg = {
    heading: "Unsupported File Type",
    description: "Sorry I can't read that, try saving it as a different extension!"
};

/**
 * Creates a warning message for bad files
 * @param {Array} badFiles - Array of bad file objects with name property
 * @param {Object} readErrorMsg - Object containing heading and description for the error message
 * @returns {Object} - An object with the warning message content
 */
export const createBadFilesWarning = (badFiles, readErrorMsg = defaultReadErrorMsg) => {
    if (badFiles.length === 0) {
        return null;
    }
    
    return {
        heading: readErrorMsg.heading,
        description: readErrorMsg.description,
        files: badFiles.map(badFile => ({
            name: badFile.name,
            reason: badFile.reason || 'Unsupported file type'
        }))
    };
};

/**
 * Appends a timestamp to a filename while preserving its extension
 * @param {string} fileName - The original file name
 * @returns {string} - The file name with timestamp appended
 */
export const appendTimestampToFileName = (fileName) => {
    const timestamp = new Date()
        .toISOString()
        .replace(/[:|-]/g, "")
        .replace(/\..*$/, "");
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
    const ext = fileName.split(".").pop();
    return `${nameWithoutExt}_${timestamp}.${ext}`;
};

/**
 * Processes a file by reading it as a DataURL
 * @param {File} file - The file to process
 * @returns {Promise<Object>} - A promise that resolves to an object with file properties and content
 */
export const processFile = (file) => {
    return new Promise((resolve, reject) => {
        let fileName = file.name;
        // Check if the file is an image with generic name
        if (
            fileName.toLowerCase() === "image.png" ||
            fileName.toLowerCase() === "image.jpg" ||
            fileName.toLowerCase() === "image.jpeg"
        ) {
            fileName = appendTimestampToFileName(file.name);
        }

        const processedFile = {
            resourceName: fileName,
            type: file.type,
            size: file.size,
            //lastModified: file.lastModified
            uploading: true
        };
        
        const reader = new FileReader();
        reader.onload = (e) => {
            processedFile.content = e.currentTarget.result;
            resolve(processedFile);
        };
        reader.onerror = () => {
            reject(new Error('Failed to read file as DataURL'));
        };
        reader.readAsDataURL(file);
    });
};

/**
 * Processes an array of files by reading each as a DataURL
 * @param {Array<File>} files - Array of files to process
 * @returns {Promise<Array<Object>>} - Promise resolving to array of processed files
 */
export const processFiles = async (files) => {
    const processedFiles = await Promise.all(
        files.map(file => 
            processFile(file)
                .catch(error => {
                    debugLog.error(`Error processing file ${file.name}:`, error);
                    return { 
                        error: true, 
                        name: file.name, 
                        reason: error.message 
                    };
                })
        )
    );
    
    return processedFiles.filter(file => !file.error);
};